import { PRODUCTS_INDEX_NAME } from "./instantsearchClient";
import { MULTI_SELECT_FACETS } from "./queryConfig";

export interface PlpRouteState {
  q?: string;
  product?: string;
  stickerType?: string[];
  lamination?: string[];
  materials?: string[];
  benefits?: string[];
  plpFlag?: string[];
}

type RefinementList = Record<string, string[]>;

// instantsearch.js doesn't expose the router/history types via a stable module
// path. We only touch `query` and `refinementList`, so a local shape is more
// portable than wrestling with internal `/es/...` imports.
interface PlpIndexUiState {
  query?: string;
  refinementList?: RefinementList;
}

export interface PlpUiState {
  [indexName: string]: PlpIndexUiState;
}

// On /stickers the productType is baked via <Configure filters> and never
// appears in refinementList. Without telling the routing layer, the orphan
// stickerType strip below would silently drop stickerType from every URL the
// user writes on /stickers. bakedAttributes carries that baked state through.
export interface PlpStateMappingOptions {
  bakedAttributes?: Readonly<Record<string, string>>;
}

interface PlpStateMapping {
  stateToRoute(uiState: PlpUiState): PlpRouteState;
  routeToState(routeState: PlpRouteState): PlpUiState;
}

function readArray(refinementList: RefinementList | undefined, key: string): string[] | undefined {
  const value = refinementList?.[key];
  if (!value || value.length === 0) return undefined;
  return value;
}

// qs.parse returns a string for `?materials=Glitter` (single) and a string[]
// for `?materials=Glitter&materials=Holographic` (multi). InstantSearch expects
// arrays for refinementList, so normalize defensively here.
function normalizeMultiValue(value: string | string[] | undefined): string[] | undefined {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) return value.length === 0 ? undefined : value;
  return value.length === 0 ? undefined : [value];
}

// qs.parse can also return nested ParsedQs objects for URLs like
// `?materials[evil]=1`. Without this guard a downstream cast would let the
// nested object reach normalizeMultiValue and crash on `.length`.
function pickStringOrArray(raw: unknown): string | string[] | undefined {
  if (raw === undefined) return undefined;
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw) && raw.every(item => typeof item === "string")) return raw;
  return undefined;
}

function activeProductType(
  refinementProduct: string | undefined,
  bakedProductType: string | undefined,
): string | undefined {
  return refinementProduct ?? bakedProductType;
}

function buildRouteFromState(uiState: PlpUiState, bakedProductType: string | undefined): PlpRouteState {
  const indexUiState = uiState[PRODUCTS_INDEX_NAME] ?? {};
  const refinementList = indexUiState.refinementList;
  const route: PlpRouteState = {};

  const query = indexUiState.query;
  if (query && query.length > 0) route.q = query;

  const refinementProduct = refinementList?.productType?.[0];
  if (refinementProduct) route.product = refinementProduct;

  const activeProduct = activeProductType(refinementProduct, bakedProductType);
  for (const key of MULTI_SELECT_FACETS) {
    const values = readArray(refinementList, key);
    if (!values) continue;
    if (key === "stickerType" && activeProduct !== "Stickers") continue;
    route[key] = values;
  }
  return route;
}

function buildRefinementListFromRoute(routeState: PlpRouteState, bakedProductType: string | undefined): RefinementList {
  const refinementList: RefinementList = {};
  if (routeState.product) refinementList.productType = [routeState.product];

  const activeProduct = activeProductType(routeState.product, bakedProductType);
  for (const key of MULTI_SELECT_FACETS) {
    const values = normalizeMultiValue(routeState[key]);
    if (!values) continue;
    if (key === "stickerType" && activeProduct !== "Stickers") continue;
    refinementList[key] = values;
  }
  return refinementList;
}

function buildStateFromRoute(routeState: PlpRouteState, bakedProductType: string | undefined): PlpUiState {
  const refinementList = buildRefinementListFromRoute(routeState, bakedProductType);
  // Only attach refinementList when non-empty so an empty URL doesn't overwrite
  // initialUiState seeded by the page (e.g. /products with q= and filters).
  const indexState: PlpIndexUiState = {};
  if (Object.keys(refinementList).length > 0) indexState.refinementList = refinementList;
  if (routeState.q && routeState.q.length > 0) indexState.query = routeState.q;
  return { [PRODUCTS_INDEX_NAME]: indexState };
}

export function createPlpStateMapping(options: PlpStateMappingOptions = {}): PlpStateMapping {
  const bakedProductType = options.bakedAttributes?.productType;
  return {
    stateToRoute: uiState => buildRouteFromState(uiState, bakedProductType),
    routeToState: routeState => buildStateFromRoute(routeState, bakedProductType),
  };
}

// Default mapping for the common case (no baked attributes).
export const PLP_STATE_MAPPING = createPlpStateMapping();

// History router options for InstantSearchNext. Overrides the default qs
// serializer to use `arrayFormat: "repeat"`, producing the PRD §3.2 URL format
// (`?materials=Glitter&materials=Holographic`) instead of the qs default
// (`?materials[0]=Glitter&materials[1]=Holographic`).
interface QsModule {
  stringify(obj: unknown, options?: { arrayFormat?: "repeat" | "indices" | "brackets" | "comma" }): string;
  parse(input: string, options?: { arrayLimit?: number }): Record<string, unknown>;
}

interface CreateUrlArgs {
  qsModule: QsModule;
  routeState: PlpRouteState;
  location: Location;
}

interface ParseUrlArgs {
  qsModule: QsModule;
  location: Location;
}

export const PLP_HISTORY_ROUTER_OPTIONS = {
  writeDelay: 100,
  createURL({ qsModule, routeState, location }: CreateUrlArgs): string {
    const queryString = qsModule.stringify(routeState, { arrayFormat: "repeat" });
    const { protocol, hostname, port = "", pathname, hash } = location;
    const portWithPrefix = port === "" ? "" : `:${port}`;
    if (!queryString) {
      return `${protocol}//${hostname}${portWithPrefix}${pathname}${hash}`;
    }
    return `${protocol}//${hostname}${portWithPrefix}${pathname}?${queryString}${hash}`;
  },
  parseURL({ qsModule, location }: ParseUrlArgs): PlpRouteState {
    const parsed = qsModule.parse(location.search.slice(1), { arrayLimit: 99 });
    const result: PlpRouteState = {};
    if (typeof parsed.q === "string") result.q = parsed.q;
    if (typeof parsed.product === "string") result.product = parsed.product;
    for (const key of MULTI_SELECT_FACETS) {
      const normalized = normalizeMultiValue(pickStringOrArray(parsed[key]));
      if (normalized) result[key] = normalized;
    }
    return result;
  },
};
